import { useState } from "react";
import { ArrowRight, ChevronDown, Clapperboard, FileText, Mail, CalendarDays, ShieldCheck, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { ConnectionBrandMark } from "@/components/connections/ConnectionBrandMark";
import { analytics } from "@/lib/analytics";
import higgsfield from "@/assets/integrations/higgsfield.png";
import windsor from "@/assets/integrations/windsor.png";

// Curated against the public release profile and October capability contracts.
// Logos describe connections, not partnerships. Buffer / OneDrive / Spotify are
// deliberately excluded while unavailable. Availability still depends on the
// connected account, platform, permissions and provider entitlement.
const CONNECTIONS = [
  ["gmail", "Gmail"], ["outlook", "Outlook"], ["google_calendar", "Google Calendar"],
  ["google_drive", "Google Drive"], ["whatsapp", "WhatsApp"], ["notion", "Notion"],
  ["higgsfield", "Higgsfield"], ["todoist", "Todoist"],
] as const;
const MORE_CONNECTIONS = [
  ["dropbox", "Dropbox"], ["trello", "Trello"], ["slack", "Slack"],
  ["microsoft_teams", "Microsoft Teams"], ["microsoft_todo", "Microsoft To Do"],
  ["google_tasks", "Google Tasks"], ["windsor", "Windsor.ai"], ["strava", "Strava"],
] as const;
const SCENARIOS = [
  { id: "email", Icon: Mail, providers: [["gmail", "Gmail"], ["outlook", "Outlook"]] },
  { id: "calendar", Icon: CalendarDays, providers: [["google_calendar", "Google Calendar"], ["outlook", "Outlook"]] },
  { id: "video", Icon: Clapperboard, providers: [["higgsfield", "Higgsfield"]] },
  { id: "documents", Icon: FileText, providers: [["google_drive", "Google Drive"], ["notion", "Notion"]] },
] as const;

function Brand({ provider }: { provider: string }) {
  const creator = provider === "higgsfield" ? higgsfield : provider === "windsor" ? windsor : null;
  return creator ? <span className="connected-brand" data-brand={provider} aria-hidden="true"><img src={creator} alt="" width={40} height={40} decoding="async" /></span>
    : <ConnectionBrandMark provider={provider} size="sm" className="connected-brand" />;
}

export function LandingActionDemo() {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(0);
  const scenario = SCENARIOS[selected];
  return <div className="connected-demo">
    <div className="connected-scenarios" role="group" aria-label={t("landing.v6.demo.choose")}>
      {SCENARIOS.map(({ id, Icon }, index) => <button key={id} type="button" aria-pressed={selected === index} onClick={() => {
        setSelected(index);
        analytics.landingConnectedExample(id);
      }}><Icon size={16} aria-hidden="true" />{t(`landing.v6.demo.${id}.label`)}</button>)}
    </div>
    <div className="connected-scene" data-scenario={scenario.id}>
      <img src="/landing/v3/voice-lifestyle-1000.webp" srcSet="/landing/v3/voice-lifestyle-600.webp 600w, /landing/v3/voice-lifestyle-1000.webp 1000w" sizes="(max-width: 767px) calc(100vw - 40px), (max-width: 1280px) 44vw, 554px" width={1000} height={1250} alt="" loading="eager" decoding="async" />
      <span className="connected-scene-label">VYV / {t("landing.v6.demo.label")}</span>
      <div className="connected-conversation" aria-live="polite" aria-atomic="true">
        <div key={scenario.id} className="connected-conversation-inner">
          <p className="connected-request">“{t(`landing.v6.demo.${scenario.id}.command`)}”</p>
          <div className="connected-response">
            <div className="connected-provider-row">{scenario.providers.map(([provider, name]) => <span key={provider}><Brand provider={provider} /><span>{name}</span></span>)}</div>
            <p><Sparkles size={17} aria-hidden="true" />{t(`landing.v6.demo.${scenario.id}.result`)}</p>
            <div className="connected-review"><ShieldCheck size={15} aria-hidden="true" />{t(`landing.v6.demo.${scenario.id}.review`)}</div>
          </div>
        </div>
      </div>
    </div>
    <p className="connected-demo-note">{t("landing.v6.demo.note")}</p>
  </div>;
}

export function LandingConnections() {
  const { t } = useLanguage();
  const brands = (items: readonly (readonly [string, string])[]) => <ul className="connected-logos">{items.map(([provider, name]) => <li key={provider}><Brand provider={provider} /><span>{name}</span></li>)}</ul>;
  return <section id="connections" className="connected-section" aria-labelledby="connections-title">
    <div className="connected-section-inner">
      <div className="connected-heading"><div><p className="landing-eyebrow">{t("landing.v6.connections.kicker")}</p><h2 id="connections-title">{t("landing.v6.connections.title")}</h2></div><p>{t("landing.v6.connections.body")}</p></div>
      {brands(CONNECTIONS)}
      <details className="connected-more"><summary>{t("landing.v6.connections.more")}<ChevronDown size={17} aria-hidden="true" /></summary>{brands(MORE_CONNECTIONS)}</details>
      <p className="connected-availability">{t("landing.v6.connections.note")}</p>
    </div>
  </section>;
}

export function LandingCreator({ onStart }: { onStart: () => void }) {
  const { t } = useLanguage();
  return <section className="landing-section connected-creator" aria-labelledby="creator-title">
    <div className="connected-creator-copy"><p className="landing-eyebrow">{t("landing.v6.creator.kicker")}</p><h2 id="creator-title">{t("landing.v6.creator.title")}</h2><p className="landing-section-description">{t("landing.v6.creator.body")}</p><button className="landing-primary" onClick={onStart}>{t("landing.v6.creator.cta")}<ArrowRight size={18} aria-hidden="true" /></button><p className="connected-creator-note">{t("landing.v6.creator.note")}</p></div>
    <div className="connected-creator-flow"><div className="connected-creator-brand"><Brand provider="higgsfield" /><span>Higgsfield</span><Clapperboard size={23} aria-hidden="true" /></div><ol>{[1,2,3].map(n => <li key={n}><span>0{n}</span><div><h3>{t(`landing.v6.creator.step${n}.title`)}</h3><p>{t(`landing.v6.creator.step${n}.body`)}</p></div></li>)}</ol></div>
  </section>;
}

export function LandingControl() {
  const { t } = useLanguage();
  return <section className="landing-section connected-control" aria-labelledby="control-title"><ShieldCheck size={32} aria-hidden="true" /><div><p className="landing-eyebrow">{t("landing.v6.control.kicker")}</p><h2 id="control-title">{t("landing.v6.control.title")}</h2><p>{t("landing.v6.control.body")}</p></div><a href="/privacy">{t("landing.v6.control.link")}<ArrowRight size={17} aria-hidden="true" /></a></section>;
}
